import { Plugin } from '@tiptap/pm/state';

export interface ValidPositions {
  beforeMatch?: string[];
  afterMatch?: string[];
}

export interface TransactionFilterOptions {
  nodeName: string;
  validPositions?: ValidPositions;
  /** Additional node type names that are allowed as neighbors (e.g. group section name). */
  allowedNeighborNodes?: string[];
}

/**
 * Creates a ProseMirror plugin that filters transactions to prevent invalid custom section insertions
 * - Prevents nesting of custom sections within other custom sections
 * - Validates neighbor nodes based on validPositions configuration
 * - Allows insertion next to other custom sections or specified neighbor node types
 */
export const createCustomSectionTransactionFilter = (
  options: TransactionFilterOptions,
): Plugin => {
  return new Plugin({
    filterTransaction: (tr, state) => {
      const customSectionType = state.schema.nodes[options.nodeName];
      const isPasteOrDrop =
        (tr as any).getMeta?.('uiEvent') === 'paste' ||
        (tr as any).getMeta?.('uiEvent') === 'drop';

      let blocked = false;

      // Resolve each step against the document *before* that step. Using only
      // `state.doc` is wrong for multi-step transactions: later `step.from`
      // values refer to intermediate documents, so `resolve` can throw and
      // would skip nesting checks if we caught and returned early.
      let docBeforeStep = state.doc;

      for (let i = 0; i < tr.steps.length; i++) {
        const step = tr.steps[i] as any;
        const slice = step.slice;

        if (slice) {
          slice.content.forEach((node: any) => {
            if (node.type === customSectionType) {
              let $pos;

              try {
                $pos = docBeforeStep.resolve(step.from);
              } catch {
                // Should be rare once positions use the correct pre-step doc;
                // reject the transaction to preserve nesting guarantees.
                blocked = true;

                return;
              }

              // Prevent nesting — always enforced, including paste/drop
              for (let d = $pos.depth; d > 0; d--) {
                if ($pos.node(d).type === customSectionType) {
                  blocked = true;

                  return;
                }
              }

              // Skip neighbor rules for paste/drop only (nesting still enforced above).
              if (isPasteOrDrop) {
                return;
              }

              const before = $pos.nodeBefore?.type.name;
              const after = $pos.nodeAfter?.type.name;

              // Allow insertion next to other custom sections or allowed neighbor nodes
              const allowedNames = [
                options.nodeName,
                ...(options.allowedNeighborNodes ?? []),
              ];

              if (
                (before && allowedNames.includes(before)) ||
                (after && allowedNames.includes(after))
              ) {
                return;
              }

              // For other positions, validate against validPositions
              const validBefore =
                !before ||
                options.validPositions?.beforeMatch?.includes(before);
              const validAfter =
                !after || options.validPositions?.afterMatch?.includes(after);

              if (!validBefore || !validAfter) {
                blocked = true;
              }
            }
          });
        }

        const applied = step.apply(docBeforeStep);

        if (applied.failed) {
          break;
        }

        docBeforeStep = applied.doc;
      }

      return !blocked;
    },
  });
};
